import React, { useState, useEffect } from 'react';
import { Mail, Lock, Eye, EyeOff, User, Scale, UserCheck, Bot, Users } from 'lucide-react';
import { UserGroupIcon, SparklesIcon, GiftIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import './Signup.css';
import logo from '../assets/logo.svg';

function Signup({ onSignup, onLoginClick }) {
  const { login } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState('client'); // 'lawyer' | 'client'
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);

  useEffect(() => {
    setFormVisible(true);
  }, []);

  const validateForm = () => {
    const nextErrors = {};

    if (!fullName.trim()) {
      nextErrors.fullName = 'Full name is required';
    } else if (fullName.trim().length < 2) {
      nextErrors.fullName = 'Full name must be at least 2 characters';
    }

    if (!email.trim()) {
      nextErrors.email = 'Email is required';
    } else if (!email.includes('@')) {
      nextErrors.email = 'Please enter a valid email';
    }

    if (!password) {
      nextErrors.password = 'Password is required';
    } else if (password.length < 6) {
      nextErrors.password = 'Password must be at least 6 characters';
    } else if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password) || !/[^A-Za-z0-9]/.test(password)) {
      nextErrors.password = 'Password must include uppercase, lowercase, number, and special character.';
    }

    if (!confirmPassword) {
      nextErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      nextErrors.confirmPassword = 'Passwords do not match';
    }

    if (!agreedToTerms) {
      nextErrors.terms = 'Please agree to the terms and conditions';
    }

    return nextErrors;
  };

  const mapSignupError = (rawMessage) => {
    const message = (rawMessage || '').toLowerCase();

    if (message.includes('password should contain at least one character')) {
      return {
        fieldErrors: {
          password: 'Password must include uppercase, lowercase, number, and special character.',
        },
        topError: '',
      };
    }

    if (message.includes('password') && message.includes('weak')) {
      return {
        fieldErrors: {
          password: 'Please choose a stronger password.',
        },
        topError: '',
      };
    }

    if (message.includes('user already registered') || message.includes('already been registered')) {
      return {
        fieldErrors: {
          email: 'This email is already registered. Try signing in instead.',
        },
        topError: '',
      };
    }

    return {
      fieldErrors: {},
      topError: 'Signup failed. Please try again.',
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setLoading(true);

    try {
      const validationErrors = validateForm();
      if (Object.keys(validationErrors).length > 0) {
        setFieldErrors(validationErrors);
        setLoading(false);
        return;
      }

      // Sign up with Supabase Auth — stores full_name + role in user_metadata
      // The database trigger will auto-create the user_profiles row
      const { data, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: fullName, role }
        }
      });
      if (authError) throw new Error(authError.message);

      // Explicitly upsert user_profiles with the name + role so it's always
      // set correctly regardless of whether the DB trigger exists or fires in time.
      if (data?.user?.id) {
        const { error: profileUpsertError } = await supabase.from('user_profiles').upsert({
          id: data.user.id,
          email: email.toLowerCase().trim(),
          full_name: fullName.trim(),
          role: role,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'id' });

        if (profileUpsertError) {
          // Signup can still succeed depending on auth settings; profile row is synced again on next authenticated session.
          console.error('Signup profile upsert failed:', profileUpsertError);
        }
      }

      // Redirect to login after successful signup
      onSignup(data.user, role);
    } catch (err) {
      const { fieldErrors: mappedFieldErrors, topError } = mapSignupError(err?.message);
      if (Object.keys(mappedFieldErrors).length > 0) {
        setFieldErrors(mappedFieldErrors);
      }
      setError(topError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container signup-container">
      {/* Left Panel - Welcome Section */}
      <div className="auth-left-panel">
        <div className="auth-left-content">
          <div className="auth-header">
            <img src={logo} alt="LawEase Logo" className="auth-logo-img" />
            <h1 className="auth-app-name">LawEase</h1>
          </div>

          <div className="auth-subtitle">
            <p className="auth-tagline">Join Our Legal Platform</p>
            <p className="auth-description">
              Whether you're a legal professional or seeking expert guidance, LawEase connects you with powerful AI-driven legal solutions.
            </p>
          </div>

          {/* Feature Highlights */}
          <div className="auth-features">
            <div className="feature-item">
              <div className="feature-icon"><UserGroupIcon className="w-4 h-4" /></div>
              <span>Expert Network</span>
            </div>
            <div className="feature-item">
              <div className="feature-icon"><SparklesIcon className="w-4 h-4" /></div>
              <span>AI-Powered Tools</span>
            </div>
            <div className="feature-item">
              <div className="feature-icon"><GiftIcon className="w-4 h-4" /></div>
              <span>Free to Start</span>
            </div>
          </div>

          {/* Animated Illustration Area */}
          <div className="auth-illustration">
            <div className="illustration-card">
              <div className="illustration-badge"><Scale size={28} /></div>
              <div className="illustration-text">For Lawyers</div>
            </div>
            <div className="illustration-card illustration-card-2">
              <div className="illustration-badge"><UserCheck size={28} /></div>
              <div className="illustration-text">For Clients</div>
            </div>
            <div className="illustration-card illustration-card-3">
              <div className="illustration-badge"><Bot size={28} /></div>
              <div className="illustration-text">AI-Powered</div>
            </div>
          </div>


          {/* Decorative Shapes */}
          <div className="decorative-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
          </div>

          {/* Background Waves */}
          <div className="auth-waves">
            <div className="wave wave-1"></div>
            <div className="wave wave-2"></div>
            <div className="wave wave-3"></div>
          </div>
        </div>
      </div>

      {/* Right Panel - Signup Form */}
      <div className="auth-right-panel">
        <div className={`auth-form-wrapper ${formVisible ? 'visible' : ''}`}>
          <div className="auth-form-header">
            <h2>Create Account</h2>
            <p>Join LawEase and get started</p>
          </div>

          {error && <div className="auth-error-message">{error}</div>}

          <form onSubmit={handleSubmit} className="auth-form signup-form">
            {/* Full Name Input */}
            <div className="form-group">
              <label htmlFor="fullName">Full Name</label>
              <div className="input-wrapper">
                <User className="input-icon" size={18} />
                <input
                  id="fullName"
                  type="text"
                  placeholder="John Doe"
                  value={fullName}
                  onChange={(e) => {
                    setFullName(e.target.value);
                    if (fieldErrors.fullName) {
                      setFieldErrors((prev) => ({ ...prev, fullName: '' }));
                    }
                  }}
                  disabled={loading}
                  className={`auth-input ${fieldErrors.fullName ? 'input-error' : ''}`}
                />
              </div>
              {fieldErrors.fullName && <p className="field-error-text">{fieldErrors.fullName}</p>}
            </div>

            {/* Email Input */}
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <div className="input-wrapper">
                <Mail className="input-icon" size={18} />
                <input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (fieldErrors.email) {
                      setFieldErrors((prev) => ({ ...prev, email: '' }));
                    }
                  }}
                  disabled={loading}
                  className={`auth-input ${fieldErrors.email ? 'input-error' : ''}`}
                />
              </div>
              {fieldErrors.email && <p className="field-error-text">{fieldErrors.email}</p>}
            </div>

            {/* Password Input */}
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <Lock className="input-icon" size={18} />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (fieldErrors.password || fieldErrors.confirmPassword) {
                      setFieldErrors((prev) => ({
                        ...prev,
                        password: '',
                        confirmPassword: '',
                      }));
                    }
                  }}
                  disabled={loading}
                  className={`auth-input ${fieldErrors.password ? 'input-error' : ''}`}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff size={22} />
                  ) : (
                    <Eye size={22} />
                  )}
                </button>
              </div>
              {fieldErrors.password && <p className="field-error-text">{fieldErrors.password}</p>}
            </div>

            {/* Confirm Password Input */}
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="input-wrapper">
                <Lock className="input-icon" size={18} />
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (fieldErrors.confirmPassword) {
                      setFieldErrors((prev) => ({ ...prev, confirmPassword: '' }));
                    }
                  }}
                  disabled={loading}
                  className={`auth-input ${fieldErrors.confirmPassword ? 'input-error' : ''}`}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={loading}
                >
                  {showConfirmPassword ? (
                    <EyeOff size={22} />
                  ) : (
                    <Eye size={22} />
                  )}
                </button>
              </div>
              {fieldErrors.confirmPassword && <p className="field-error-text">{fieldErrors.confirmPassword}</p>}
            </div>

            {/* Role Selector */}
            <div className="form-group role-selector-group">
              <label>I am a...</label>
              <div className="role-selector">
                <button
                  type="button"
                  className={`role-pill ${role === 'lawyer' ? 'active' : ''}`}
                  onClick={() => setRole('lawyer')}
                  disabled={loading}
                >
                  <Scale className="role-icon" size={18} />
                  <span>Lawyer</span>
                </button>
                <button
                  type="button"
                  className={`role-pill ${role === 'client' ? 'active' : ''}`}
                  onClick={() => setRole('client')}
                  disabled={loading}
                >
                  <Users className="role-icon" size={18} />
                  <span>Client</span>
                </button>
              </div>
              <p className="role-description">
                {role === 'lawyer'
                  ? 'Access professional tools, case management, and document automation.'
                  : 'Find legal experts, get advice, and manage your legal matters.'}
              </p>
            </div>

            {/* Terms & Conditions */}
            <div className="form-footer">
              <label className={`terms-checkbox ${fieldErrors.terms ? 'terms-checkbox-error' : ''}`}>
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => {
                    setAgreedToTerms(e.target.checked);
                    if (fieldErrors.terms) {
                      setFieldErrors((prev) => ({ ...prev, terms: '' }));
                    }
                  }}
                  disabled={loading}
                />
                <span>
                  I agree to the{' '}
                  <a href="/legal/terms-of-service.html" className="terms-link" target="_blank" rel="noopener noreferrer">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="/legal/privacy-policy.html" className="terms-link" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                </span>
              </label>
              {fieldErrors.terms && <p className="field-error-text">{fieldErrors.terms}</p>}
            </div>

            {/* Sign Up Button */}
            <button
              type="submit"
              className="auth-submit-btn"
              disabled={loading}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          {/* Sign In Link */}
          <div className="auth-footer-text">
            <p>
              Already have an account?{' '}
              <button
                type="button"
                className="auth-link-btn"
                onClick={onLoginClick}
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup;